import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { COLLAPSE_MS } from '../Motion';

/** A computed length in px; an unset one counts as 0. */
const px = (value: string): number => Number.parseFloat(value) || 0;

/** True while the feed's content is taller than the feed. Measured from the
 *  children, not scrollHeight — a bottom-anchored flex box overflows
 *  upwards, and browsers leave upward overflow out of scrollHeight. */
export function feedOverflows(el: HTMLElement): boolean {
  let contentHeight = 0;
  for (const child of Array.from(el.children)) {
    const style = getComputedStyle(child);
    contentHeight +=
      child.getBoundingClientRect().height +
      px(style.marginTop) +
      px(style.marginBottom);
  }
  const style = getComputedStyle(el);
  const inner =
    el.clientHeight - px(style.paddingTop) - px(style.paddingBottom);
  return contentHeight > inner + 1;
}

/** feedOverflows, kept current: the top fade only belongs on a window with
 *  something hidden above. The feed re-renders exactly when its content
 *  changes, so measuring after each render is enough; an unchanged value
 *  bails out without re-rendering. */
export function useIsOverflowing(ref: RefObject<HTMLElement | null>): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setIsOverflowing(feedOverflows(el));
  });
  return isOverflowing;
}

/** Long enough for a part's fold to finish, so the next measure sees the
 *  room it freed. */
export const FOLD_SETTLE_MS = COLLAPSE_MS + 80;

/**
 * Instead of letting the live note push the chain off the top, fold its
 * oldest open part, then the next, one at a time, while the feed still
 * overflows. Each fold waits for the last to finish and measures again, so
 * only as much folds as the room needs. The part being written is never
 * folded; a new step starts with every part open.
 *
 * The feed is measured after every render, not when an overflow flag
 * changes: the live part grows on every chunk, and after a fold that made
 * room it can overflow again without any flag ever turning false.
 */
export function useFoldForRoom(
  feedRef: RefObject<HTMLElement | null>,
  noteKey: string,
  partCount: number
): number {
  const [fold, setFold] = useState({ key: noteKey, count: 0 });
  const folded = fold.key === noteKey ? fold.count : 0;
  const pending = useRef<number | null>(null);
  const lastFoldAt = useRef(Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (pending.current !== null || folded >= partCount - 1) return;
    const el = feedRef.current;
    if (!el || !feedOverflows(el)) return;
    // Let the previous fold finish before measuring for the next.
    const wait = Math.max(0, lastFoldAt.current + FOLD_SETTLE_MS - Date.now());
    pending.current = window.setTimeout(() => {
      pending.current = null;
      const feed = feedRef.current;
      if (feed && feedOverflows(feed)) {
        lastFoldAt.current = Date.now();
        setFold({ key: noteKey, count: folded + 1 });
      }
    }, wait);
  });

  // A new step (or unmount) drops whatever the last step had in flight: its
  // pending fold and its settle clock. Cleanups run before this commit's
  // effects, so the measure above then schedules the new step's own fold.
  useEffect(
    () => () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
      pending.current = null;
      lastFoldAt.current = Number.NEGATIVE_INFINITY;
    },
    [noteKey]
  );
  return folded;
}
